// Item frames (UX §6.3): a 1×1 cell, content box = cell minus itemPad on each side, scaled by tier
// 64% → 100%; fill boxes are square and centred. Loose items get the cream sticker halo, tier ≥ rimMinTier
// the warm rim light and tier ≥ glowMinTier the glow. Family modules describe parts in box-relative
// coordinates (0–1 of the fill box) through this kit.
import type { Ctx2D } from '../ctx.ts';
import type { ArtPalette, Swatch } from '../palette.ts';
import { drawParts, glowFor, outlineUnits, type Part, part } from '../passes.ts';
import { mulberry32, type Rand } from '../rng.ts';
import {
  type Box,
  box,
  ellipse,
  line,
  type PathFn,
  type Pt,
  poly,
  roundPoly,
  rrect,
  smoothPoly,
} from '../shapes.ts';
import type { ArtEnv, ItemSpec } from '../types.ts';

export interface ItemKit {
  c: Ctx2D;
  pal: ArtPalette;
  rng: Rand;
  tier: number;
  tiers: number;
  /** Fill box (units). */
  x: number;
  y: number;
  s: number;
  /** Visible outline width (units); details use half of it. */
  ow: number;
  X(f: number): number;
  Y(f: number): number;
  S(f: number): number;
  P(p: readonly [number, number]): Pt;
  sw(tok: string): Swatch;
  /** Box-relative rounded rect. */
  rect(
    tok: string,
    fx: number,
    fy: number,
    fw: number,
    fh: number,
    fr: number | number[],
    extra?: Partial<Part>,
  ): Part;
  ell(tok: string, fcx: number, fcy: number, frx: number, fry: number, extra?: Partial<Part>): Part;
  poly(
    tok: string,
    pts: readonly (readonly [number, number])[],
    fr: number,
    extra?: Partial<Part>,
  ): Part;
  smooth(tok: string, pts: readonly Pt[], extra?: Partial<Part>): Part;
  /** A polygon in absolute units with rounded corners (radius r units). */
  polyAbs(tok: string, pts: readonly Pt[], r: number, extra?: Partial<Part>): Part;
  /** A box-relative open stroke (details). */
  stroke(pts: readonly (readonly [number, number])[], color: string, w: number): void;
  fillEll(fcx: number, fcy: number, frx: number, fry: number, color: string): void;
  fillRect(fx: number, fy: number, fw: number, fh: number, fr: number, color: string): void;
}

export function itemScale(tier: number, tiers: number, min: number): number {
  return tiers <= 1 ? 1 : min + ((1 - min) * (tier - 1)) / (tiers - 1);
}

export function makeKit(
  c: Ctx2D,
  spec: ItemSpec,
  px: number,
  env: ArtEnv,
  fillFrac: number,
): ItemKit {
  const { pal, sprites } = env;
  const s = fillFrac * itemScale(spec.tier, spec.tiers, sprites.itemScaleMin) * 100;
  const x = 50 - s / 2;
  const y = 50 - s / 2;
  const X = (f: number) => x + f * s;
  const Y = (f: number) => y + f * s;
  const S = (f: number) => f * s;
  const P = (p: readonly [number, number]): Pt => [X(p[0]), Y(p[1])];
  const ow = outlineUnits(sprites.outline, sprites.outlineMinPx, px, env.dpr);
  const bboxOf = (pts: readonly Pt[]): Box => {
    const xs = pts.map((p) => p[0]);
    const ys = pts.map((p) => p[1]);
    return box(
      Math.min(...xs),
      Math.min(...ys),
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
    );
  };
  const kit: ItemKit = {
    c,
    pal,
    rng: mulberry32(spec.seed),
    tier: spec.tier,
    tiers: spec.tiers,
    x,
    y,
    s,
    ow,
    X,
    Y,
    S,
    P,
    sw: (t) => pal.sw(t),
    rect(tok, fx, fy, fw, fh, fr, extra) {
      const r = typeof fr === 'number' ? S(fr) : fr.map(S);
      return part(
        rrect(X(fx), Y(fy), S(fw), S(fh), r),
        pal.sw(tok),
        box(X(fx), Y(fy), S(fw), S(fh)),
        extra,
      );
    },
    ell(tok, fcx, fcy, frx, fry, extra) {
      return part(
        ellipse(X(fcx), Y(fcy), S(frx), S(fry)),
        pal.sw(tok),
        box(X(fcx - frx), Y(fcy - fry), S(2 * frx), S(2 * fry)),
        extra,
      );
    },
    poly(tok, pts, fr, extra) {
      const abs = pts.map(P);
      const path: PathFn = fr > 0 ? roundPoly(abs, S(fr)) : poly(abs);
      return part(path, pal.sw(tok), bboxOf(abs), extra);
    },
    polyAbs(tok, pts, r, extra) {
      return part(roundPoly(pts, r), pal.sw(tok), bboxOf(pts), extra);
    },
    smooth(tok, pts, extra) {
      return part(smoothPoly(pts), pal.sw(tok), bboxOf(pts), extra);
    },
    stroke(pts, color, w) {
      c.beginPath();
      line(pts.map(P))(c);
      c.strokeStyle = color;
      c.lineWidth = w;
      c.lineCap = 'round';
      c.lineJoin = 'round';
      c.stroke();
    },
    fillEll(fcx, fcy, frx, fry, color) {
      c.beginPath();
      ellipse(X(fcx), Y(fcy), S(frx), S(fry))(c);
      c.fillStyle = color;
      c.fill();
    },
    fillRect(fx, fy, fw, fh, fr, color) {
      c.beginPath();
      rrect(X(fx), Y(fy), S(fw), S(fh), S(fr))(c);
      c.fillStyle = color;
      c.fill();
    },
  };
  return kit;
}

/** Draws an item frame: kit → family parts → passes (halo, glow, outline, fills). */
export function drawItemParts(
  c: Ctx2D,
  spec: ItemSpec,
  px: number,
  env: ArtEnv,
  build: (k: ItemKit) => Part[],
): void {
  const { pal, sprites } = env;
  c.save();
  c.scale(px / 100, px / 100);
  const k = makeKit(c, spec, px, env, 1 - 2 * sprites.itemPad);
  const parts = build(k);
  drawParts(c, parts, {
    ow: k.ow,
    rim: spec.tier >= pal.rules.rimMinTier,
    halo: { w: sprites.halo * 100, color: pal.hex('ui.cream'), alpha: sprites.haloAlpha },
    glow: glowFor(pal, pal.tier(spec.tier), spec.tier, px),
    shadeBand: pal.rules.shadeBand,
    hiW: Math.max(2.2, k.s * 0.06),
  });
  c.restore();
}
